import { http } from "@/lib/api";

// Operation-scoped fallback messages. Displayed only when the backend did
// not return a human-readable detail.message; otherwise the authoritative
// backend message is preserved verbatim so athletes see the real reason.
const FALLBACK_MESSAGES = {
  generation: "Could not generate a training plan draft.",
  confirmation: "Could not confirm the training plan.",
  materialisation: "Could not prepare the training plan for activation.",
  activation: "Could not activate the training plan.",
  replacement: "Could not replace the active training plan.",
  read: "Could not load your training plan.",
};

const normaliseError = (err, operation = "generation") => {
  if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return { code: "aborted", message: "Request cancelled" };
  }
  if (err?.code === "ERR_BACKEND_URL_MISSING") {
    return {
      code: "config",
      message: "Coach is not configured. Set REACT_APP_BACKEND_URL and reload.",
    };
  }
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
    return {
      code: "network",
      message: "Cannot connect to the Endurava server.",
    };
  }
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const backendCode = detail && typeof detail === "object" ? detail.code : undefined;
  const fallback = FALLBACK_MESSAGES[operation] || FALLBACK_MESSAGES.generation;
  const message =
    (typeof detail === "string" && detail)
    || (detail && typeof detail === "object" && detail.message)
    || fallback;
  if (status === 401) {
    return {
      code: "unauthenticated",
      status,
      message: "Your session has expired. Please sign in again.",
    };
  }
  return { code: backendCode || "unknown", status, message };
};

const wrapError = (err, operation = "generation") => {
  const info = normaliseError(err, operation);
  const wrapped = new Error(info.message);
  wrapped.planError = info;
  wrapped.status = err?.response?.status;
  throw wrapped;
};

export const generateTrainingPlan = async ({
  horizonWeeks = 1,
  blockStartDate,
  clientRequestId,
} = {}) => {
  try {
    const payload = { horizon_weeks: horizonWeeks };
    if (blockStartDate) payload.block_start_date = blockStartDate;
    if (clientRequestId) payload.client_request_id = clientRequestId;
    const { data } = await http.post("/ai-coach/generate-training-plan", payload);
    return data;
  } catch (err) {
    wrapError(err, "generation");
  }
  return null;
};

export const fetchCurrentTrainingPlanDraft = async () => {
  try {
    const { data } = await http.get("/ai-coach/training-plan-drafts/current");
    return data;
  } catch (err) {
    // 404 simply means the athlete has no current validated draft yet.
    if (err?.response?.status === 404) return null;
    wrapError(err, "read");
  }
  return null;
};

export const fetchCurrentTrainingPlanLifecycle = async () => {
  try {
    const { data } = await http.get("/ai-coach/training-plan-lifecycle/current");
    return data;
  } catch (err) {
    // Backend always returns 200 with nulls when there is no lifecycle,
    // but a defensive 404 handler keeps the panel usable if the endpoint
    // is unavailable (e.g. old backend). Treat any read error as "no
    // lifecycle" so the athlete can still generate a fresh plan.
    if (err?.response?.status === 404) return null;
    wrapError(err, "read");
  }
  return null;
};

export const fetchTrainingPlanDraft = async (draftRef) => {
  try {
    const { data } = await http.get(
      `/ai-coach/training-plan-drafts/${encodeURIComponent(draftRef)}`,
    );
    return data;
  } catch (err) {
    wrapError(err, "read");
  }
  return null;
};

const requireRef = (value, name) => {
  if (typeof value !== "string" || !value.trim()) {
    const err = new Error(`${name} is required`);
    err.planError = { code: "missing_ref", message: err.message };
    throw err;
  }
  return value;
};

export const confirmTrainingPlanDraft = async (
  draftRef,
  {
    clientRequestId,
    expectedCandidateId,
    expectedContextFingerprint,
  } = {},
) => {
  try {
    const ref = requireRef(draftRef, "draftRef");
    const payload = { client_request_id: clientRequestId };
    if (expectedCandidateId) payload.expected_candidate_id = expectedCandidateId;
    if (expectedContextFingerprint) {
      payload.expected_context_fingerprint = expectedContextFingerprint;
    }
    const { data } = await http.post(
      `/ai-coach/training-plan-drafts/${encodeURIComponent(ref)}/confirm`,
      payload,
    );
    return data;
  } catch (err) {
    wrapError(err, "confirmation");
  }
  return null;
};

export const materialiseTrainingPlanConfirmation = async (
  confirmationRef,
  { clientRequestId } = {},
) => {
  try {
    const ref = requireRef(confirmationRef, "confirmationRef");
    const { data } = await http.post(
      `/ai-coach/training-plan-confirmations/${encodeURIComponent(ref)}/materialise`,
      { client_request_id: clientRequestId },
    );
    return data;
  } catch (err) {
    wrapError(err, "materialisation");
  }
  return null;
};

export const activateTrainingPlanMaterialisation = async (
  materialisationRef,
  { clientRequestId } = {},
) => {
  try {
    const ref = requireRef(materialisationRef, "materialisationRef");
    const { data } = await http.post(
      `/ai-coach/training-plan-materialisations/${encodeURIComponent(ref)}/activate`,
      { client_request_id: clientRequestId },
    );
    return data;
  } catch (err) {
    wrapError(err, "activation");
  }
  return null;
};

export const replaceActiveTrainingPlan = async (
  materialisationRef,
  { clientRequestId, expectedCurrentBlockId } = {},
) => {
  try {
    const ref = requireRef(materialisationRef, "materialisationRef");
    if (!expectedCurrentBlockId) {
      const err = new Error("expectedCurrentBlockId is required");
      err.planError = { code: "missing_expected_block", message: err.message };
      throw err;
    }
    const { data } = await http.post(
      `/ai-coach/training-plan-materialisations/${encodeURIComponent(ref)}/replace-active-plan`,
      {
        client_request_id: clientRequestId,
        expected_current_block_id: expectedCurrentBlockId,
      },
    );
    return data;
  } catch (err) {
    wrapError(err, "replacement");
  }
  return null;
};

export const fetchCurrentActiveTrainingPlan = async () => {
  try {
    const { data } = await http.get("/training-block/current/active-plan");
    return data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    wrapError(err, "read");
  }
  return null;
};

export const fetchCurrentTrainingBlock = async () => {
  try {
    const { data } = await http.get("/training-block/current");
    return data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    wrapError(err, "read");
  }
  return null;
};

/**
 * Athlete edits one future planned session. Supports duration reduction,
 * difficulty selection, optional reason, and full structured prescription
 * overrides (warm-up / main set / cool-down).
 */
export const editPlannedSession = async (
  sessionId,
  {
    durationMinutes,
    difficulty,
    reason,
    prescriptionOverride,
    resetPrescription,
    libraryWorkoutCode,
    libraryDifficulty,
    libraryCustomSteps,
  } = {},
) => {
  try {
    const ref = requireRef(sessionId, "sessionId");
    const body = {};
    if (durationMinutes != null) body.duration_minutes = durationMinutes;
    if (difficulty) body.difficulty = difficulty;
    if (reason != null) body.reason = reason;
    if (prescriptionOverride) body.prescription_override = prescriptionOverride;
    if (resetPrescription) body.reset_prescription = true;
    if (libraryWorkoutCode) body.library_workout_code = libraryWorkoutCode;
    if (libraryDifficulty) body.library_difficulty = libraryDifficulty;
    if (libraryCustomSteps) body.library_custom_steps = libraryCustomSteps;
    const { data } = await http.patch(
      `/training-block/planned-session/${encodeURIComponent(ref)}`,
      body,
    );
    return data?.session ?? null;
  } catch (err) {
    wrapError(err, "read");
  }
  return null;
};

export const fetchTrainingBlockWeeks = async (blockId) => {
  try {
    const ref = requireRef(blockId, "blockId");
    const { data } = await http.get(
      `/training-block/${encodeURIComponent(ref)}/weeks`,
    );
    return data;
  } catch (err) {
    wrapError(err, "read");
  }
  return null;
};
